import type {
  Connection,
  ConnectionCreate,
  Matter,
  MatterContactConnection,
} from "@/lib/types";
import { getConnectionPreviewContact } from "./item-contact-connections";

export const MATTER_PARTY_CONNECTION_TYPES = {
  Client: "matter_to_client",
  Defendant: "matter_to_defendant",
  Driver: "matter_to_driver",
  "Emergency Contact": "matter_to_emergency_contact",
  Expert: "matter_to_expert",
  Other: "matter_to_other",
  Insured: "matter_to_insured",
  Passenger: "matter_to_passenger",
  Parent: "matter_to_parent",
  "Primary Contact": "matter_to_primary_contact",
  "Policy Holder": "matter_to_policy_holder",
  Pedestrian: "matter_to_pedestrian",
  Referral: "matter_to_referral",
  Referee: "matter_to_referee",
  Spouse: "matter_to_spouse",
  "Third Party": "matter_to_third_party",
  Witness: "matter_to_witness",
} as const;

export type SupportedMatterPartyRole =
  keyof typeof MATTER_PARTY_CONNECTION_TYPES;

const MATTER_PARTY_ROLE_BY_TYPE = Object.fromEntries(
  Object.entries(MATTER_PARTY_CONNECTION_TYPES).flatMap(([role, type]) => [
    [type, role],
    [type.replace("matter_to_", "matter_to_legacy_"), role],
  ]),
) as Record<string, SupportedMatterPartyRole>;

export const getMatterPartyConnectionRole = (connection: Connection) =>
  MATTER_PARTY_ROLE_BY_TYPE[connection.connectionType];

export const isSupportedMatterPartyRole = (
  role: string,
): role is SupportedMatterPartyRole => role in MATTER_PARTY_CONNECTION_TYPES;

type MatterPartyConnectionSource = Pick<Matter, "id"> &
  Partial<Pick<Matter, "connections">>;

export const getMatterPartyConnections = (
  matter: MatterPartyConnectionSource,
): Connection[] =>
  (matter.connections ?? []).filter(
    (connection) =>
      connection.sourceObjectType === "matter" &&
      connection.sourceObjectId === matter.id &&
      connection.targetObjectType === "contact" &&
      (connection.state === "active" || connection.state === "scheduled") &&
      connection.connectionType in MATTER_PARTY_ROLE_BY_TYPE,
  );

export const createMatterPartyConnectionInput = ({
  contactId,
  dateTimeEnd,
  dateTimeStart,
  description,
  faultStatus,
  insuranceCoverage,
  matterId,
  role,
  witnessStatus,
}: {
  contactId: string;
  dateTimeEnd?: string | null;
  dateTimeStart?: string | null;
  description?: string | null;
  faultStatus?: string | null;
  insuranceCoverage?: string | null;
  matterId: string;
  role: SupportedMatterPartyRole;
  witnessStatus?: string | null;
}): ConnectionCreate => ({
  connectionType: MATTER_PARTY_CONNECTION_TYPES[role],
  dateTimeEnd,
  dateTimeStart,
  description,
  faultStatus,
  insuranceCoverage,
  operationId: crypto.randomUUID(),
  sourceObjectId: matterId,
  sourceObjectType: "matter",
  targetObjectId: contactId,
  targetObjectType: "contact",
  witnessStatus,
});

/**
 * Temporary presentation adapter for the existing matter-party cards and
 * modals. The source of truth is the V3 connection; no legacy row is read.
 */
export const toMatterContactConnectionPresentation = (
  connection: Connection,
  matterId: string,
): MatterContactConnection =>
  ({
    ...connection,
    contact: getConnectionPreviewContact(connection),
    contactId: connection.targetObjectId,
    firmId: "",
    label:
      MATTER_PARTY_ROLE_BY_TYPE[connection.connectionType] ??
      connection.connectionType,
    matterId,
  }) as unknown as MatterContactConnection;
